/**
 * Variant Availability Checker + Smart Navigation
 * =================================================
 * Two jobs:
 *
 * 1. VISUAL FEEDBACK: Marks option buttons/swatches as unavailable when
 *    the combination they'd create doesn't exist as a real variant.
 *    Uses Shopify's native disabled styling:
 *      - Pills: adds "disabled" class → line-through + faded
 *      - Swatches: adds "visually-disabled" class → same sold-out styling
 *    No custom CSS needed.
 *
 * 2. SMART NAVIGATION: When a customer clicks an unavailable option,
 *    intercepts the click and jumps to the closest existing variant
 *    that includes the value they clicked. Prioritizes keeping Color.
 *
 * VARIANT DATA: Read from an inline JSON blob in the page HTML, which
 * is populated from a product metafield (custom.variant_combos) written
 * by the variant_combo_metafield.py pipeline script. This metafield is
 * written via the Admin API which has no variant cap, so every product
 * gets complete data regardless of variant count.
 *
 * DATA FORMAT (compact):
 *   [{"o": ["Regular", "Black", "S"], "a": true}, ...]
 *   "o" = option values array, "a" = available (in stock)
 *
 * WORKS WITH: pill buttons, swatches, and dropdowns, including mixed
 * picker types on the same product.
 */

(function () {

  // ── Data helpers ──────────────────────────────────────────────

  /**
   * Read the variant combo data from the inline JSON blob.
   * This data comes from the product metafield, injected by Liquid.
   * Returns null if the metafield hasn't been written yet (graceful fallback).
   */
  function getVariantData(container) {
    var el = container.querySelector('[data-variant-availability-data]');
    if (!el) return null;
    try {
      var raw = JSON.parse(el.textContent);
      // Transform compact format {"o": [...], "a": bool} to
      // the format our code uses: {"options": [...], "available": bool}
      return raw.map(function (v) {
        return {
          options: v.o,
          available: v.a
        };
      });
    } catch (e) {
      console.error('Variant availability: failed to parse variant data', e);
      return null;
    }
  }

  /**
   * Read the currently selected option values from the DOM.
   */
  function getSelectedOptions(variantSelects) {
    var options = [];
    variantSelects.querySelectorAll('.product-form__input').forEach(function (group) {
      var select = group.querySelector('select');
      if (select) {
        options.push(select.value);
      } else {
        var checked = group.querySelector('input[type="radio"]:checked');
        if (checked) options.push(checked.value);
      }
    });
    return options;
  }

  /**
   * Get the option group names (e.g., ["Length", "Color", "Size"]).
   */
  function getOptionNames(variantSelects) {
    var names = [];
    variantSelects.querySelectorAll('.product-form__input').forEach(function (group) {
      var legend = group.querySelector('legend.form__label, label.form__label');
      if (legend) {
        var text = legend.textContent.trim();
        if (text.includes(':')) text = text.split(':')[0].trim();
        names.push(text);
      } else {
        names.push('');
      }
    });
    return names;
  }

  /**
   * Figure out which option group index a given input belongs to.
   */
  function getGroupIndex(input, variantSelects) {
    var groups = Array.from(variantSelects.querySelectorAll('.product-form__input'));
    var parentGroup = input.closest('.product-form__input');
    return groups.indexOf(parentGroup);
  }

  // ── Disabled class helpers ───────────────────────────────────

  function getDisabledClass(input) {
    if (input.classList.contains('swatch-input__input')) {
      return 'visually-disabled';
    }
    return 'disabled';
  }

  function markUnavailable(input) {
    input.classList.add(getDisabledClass(input));
  }

  function markAvailable(input) {
    input.classList.remove('disabled', 'visually-disabled');
  }

  // ── Smart navigation ────────────────────────────────────────

  /**
   * Find the best existing variant that includes the clicked value.
   * Prioritizes keeping Color the same.
   */
  function findBestVariant(variants, selectedOptions, clickedGroupIndex, clickedValue, optionNames) {
    var candidates = variants.filter(function (v) {
      return v.options[clickedGroupIndex] === clickedValue;
    });

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    var colorIndex = -1;
    if (optionNames) {
      for (var i = 0; i < optionNames.length; i++) {
        if (optionNames[i].toLowerCase() === 'color') {
          colorIndex = i;
          break;
        }
      }
    }

    var bestVariant = null;
    var bestScore = -1;

    candidates.forEach(function (variant) {
      var score = 0;
      for (var i = 0; i < selectedOptions.length; i++) {
        if (i === clickedGroupIndex) continue;
        if (variant.options[i] === selectedOptions[i]) {
          score += 1;
          if (i === colorIndex) score += 0.5;
        }
      }
      if (variant.available) score += 0.1;

      if (score > bestScore) {
        bestScore = score;
        bestVariant = variant;
      }
    });

    return bestVariant;
  }

  /**
   * Programmatically select all options to match a variant, then
   * trigger a change event so Shopify fetches the right HTML.
   */
  function selectVariantOptions(variantSelects, variant) {
    var groups = variantSelects.querySelectorAll('.product-form__input');
    var lastChangedInput = null;

    groups.forEach(function (group, index) {
      var targetValue = variant.options[index];
      var select = group.querySelector('select');

      if (select) {
        if (select.value !== targetValue) {
          select.value = targetValue;
          Array.from(select.options).forEach(function (opt) {
            if (opt.value === targetValue) {
              opt.setAttribute('selected', 'selected');
            } else {
              opt.removeAttribute('selected');
            }
          });
          lastChangedInput = select;
        }
      } else {
        var inputs = group.querySelectorAll('input[type="radio"]');
        var targetInput = Array.from(inputs).find(function (inp) {
          return inp.value === targetValue;
        });

        if (targetInput && !targetInput.checked) {
          targetInput.checked = true;
          lastChangedInput = targetInput;
        }
      }
    });

    if (lastChangedInput) {
      lastChangedInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ── Visual availability marking ──────────────────────────────

  function updateAvailability(variantSelects, variants) {
    var selectedOptions = getSelectedOptions(variantSelects);
    if (selectedOptions.length === 0) return;

    var existingCombos = new Set(variants.map(function (v) { return v.options.join('|'); }));
    var optionGroups = variantSelects.querySelectorAll('.product-form__input');

    optionGroups.forEach(function (group, groupIndex) {
      var select = group.querySelector('select');

      if (select) {
        Array.from(select.options).forEach(function (optionEl) {
          var testCombo = selectedOptions.slice();
          testCombo[groupIndex] = optionEl.value;
          var comboExists = existingCombos.has(testCombo.join('|'));
          optionEl.classList.toggle('combo-unavailable', !comboExists);
        });
      } else {
        group.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var testCombo = selectedOptions.slice();
          testCombo[groupIndex] = input.value;
          var comboExists = existingCombos.has(testCombo.join('|'));

          if (!comboExists) {
            markUnavailable(input);
          } else {
            var matchingVariant = variants.find(function (v) {
              return v.options.join('|') === testCombo.join('|');
            });
            if (matchingVariant && matchingVariant.available) {
              markAvailable(input);
            }
          }
        });
      }
    });
  }

  // ── Click interception ───────────────────────────────────────

  function attachClickInterceptors(variantSelects, variants) {
    var existingCombos = new Set(variants.map(function (v) { return v.options.join('|'); }));
    var optionNames = getOptionNames(variantSelects);

    variantSelects.addEventListener(
      'click',
      function (event) {
        var input = event.target.closest('input[type="radio"]');

        if (!input && event.target.closest('label')) {
          var label = event.target.closest('label');
          var forId = label.getAttribute('for');
          if (forId) {
            input = variantSelects.querySelector('#' + CSS.escape(forId));
          }
        }

        if (!input) return;

        var selectedOptions = getSelectedOptions(variantSelects);
        var groupIndex = getGroupIndex(input, variantSelects);
        if (groupIndex === -1) return;

        var testCombo = selectedOptions.slice();
        testCombo[groupIndex] = input.value;

        if (existingCombos.has(testCombo.join('|'))) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        var bestVariant = findBestVariant(
          variants,
          selectedOptions,
          groupIndex,
          input.value,
          optionNames
        );

        if (!bestVariant) {
          console.warn('Variant availability: no variant found for', input.value);
          return;
        }

        selectVariantOptions(variantSelects, bestVariant);
      },
      true
    );
  }

  // ── Initialization ───────────────────────────────────────────

  function initVariantSelects(variantSelects) {
    var container = variantSelects.closest('product-info') || variantSelects.closest('.shopify-section');
    var variants = getVariantData(container);
    if (!variants) return; // No metafield data yet — skip gracefully

    updateAvailability(variantSelects, variants);
    attachClickInterceptors(variantSelects, variants);
  }

  function init() {
    document.querySelectorAll('variant-selects').forEach(function (variantSelects) {
      initVariantSelects(variantSelects);
    });

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        setTimeout(function () {
          document.querySelectorAll('variant-selects').forEach(function (variantSelects) {
            initVariantSelects(variantSelects);
          });
        }, 100);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();